import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Icon } from './Icon';

export interface MultiSelectPillOption {
  value: string;
  /** i18n key for the label. If absent, `label` (raw string) is used. */
  labelKey?: string;
  label?: string;
}

interface MultiSelectPillsProps {
  value: string[];
  onChange: (value: string[]) => void;
  options: MultiSelectPillOption[];
  allowCustom?: boolean;
  addCustomLabelKey?: string;
  ariaLabel: string;
  className?: string;
}

// Toggle-chip multi-select (the "ChoicePill" row from the design). Each chip is
// an aria-pressed toggle button. With `allowCustom`, a dashed "+ Other" chip
// reveals an inline text input that appends a trimmed, de-duplicated value.
export function MultiSelectPills({
  value,
  onChange,
  options,
  allowCustom = false,
  addCustomLabelKey = 'fields.other',
  ariaLabel,
  className,
}: MultiSelectPillsProps) {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const [custom, setCustom] = useState('');

  function toggle(v: string) {
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  }

  function commitCustom() {
    const trimmed = custom.trim();
    if (trimmed && !value.includes(trimmed)) onChange([...value, trimmed]);
    setCustom('');
    setAdding(false);
  }

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn('flex flex-wrap items-center gap-2', className)}
    >
      {options.map((opt) => {
        const on = value.includes(opt.value);
        const label = opt.labelKey ? t(opt.labelKey) : (opt.label ?? opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={on}
            onClick={() => toggle(opt.value)}
            className={cn(
              'inline-flex min-h-[44px] items-center gap-1.5 rounded-full px-3.5 text-[13.5px] font-medium tracking-[-0.005em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
              on
                ? 'border border-ink bg-ink text-accent-foreground'
                : 'border border-line-2 bg-paper text-ink-2 hover:border-line',
            )}
          >
            {on && <Icon name="check" size={13} strokeWidth={2.4} />}
            {label}
          </button>
        );
      })}

      {allowCustom &&
        (adding ? (
          <span className="inline-flex min-h-[44px] items-center gap-1 rounded-full border border-ink bg-paper px-2 pl-3.5">
            <input
              autoFocus
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitCustom();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  setCustom('');
                  setAdding(false);
                }
              }}
              onBlur={commitCustom}
              aria-label={t(addCustomLabelKey)}
              className="w-28 bg-transparent text-[13.5px] text-ink outline-none placeholder:text-muted-2"
              placeholder={t(addCustomLabelKey)}
            />
            <button
              type="button"
              aria-label={t('common.add')}
              onClick={commitCustom}
              className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-ink text-accent-foreground"
            >
              <Icon name="check" size={13} strokeWidth={2.4} />
            </button>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border border-dashed border-line-2 bg-transparent px-3.5 text-[13.5px] font-medium text-muted transition-colors hover:text-ink-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
          >
            <Icon name="plus" size={13} />
            {t(addCustomLabelKey)}
          </button>
        ))}
    </div>
  );
}
